import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Loader2, Refrigerator } from 'lucide-react';
import FridgeHowToSheet, { FRIDGE_HOW_TO_STYLES } from 'app/javascript/components/fridge/FridgeHowToSheet';
import QrCodeImage, { type QrCodeState } from 'app/javascript/components/shared/QrCodeImage';
import PrintablePosterFooter from 'app/javascript/components/shared/PrintablePosterFooter';
import PrintablePosterLayout from 'app/javascript/components/shared/PrintablePosterLayout';
import { useFridge } from 'app/javascript/queries/useFridge';
import { ROUTES } from 'app/javascript/constants/routes';
import { formatCurrency } from 'app/javascript/utils/currency';

// A4 fits about a dozen rows in one column at poster type sizes; splitting
// earlier than that only shrinks the type while the sheet stays half empty.
const TWO_COLUMN_THRESHOLD = 12;
// Below this, spreading the rows down an A4 sheet leaves comical gaps between
// them; the list stays top-aligned instead.
const SPREAD_THRESHOLD = 4;

// Filling the sheet cannot mean stretching five rows across it: gaps three
// times the size of every other space on the page read as a layout accident.
// Padding the list inset caps how much space the spread has to give away, and
// unlike a fixed gap it can never push the menu onto a second page.
function spreadInset(count: number): string {
  if (count <= 5) return 'py-24 print:py-20';
  if (count <= 8) return 'py-12 print:py-10';
  return 'py-2';
}

// A short menu fills the sheet by being bigger, not by floating in white space —
// this is read standing in front of the fridge, so scale is what buys legibility.
const MENU_SIZES = {
  roomy: { name: 'text-3xl print:text-2xl', price: 'text-5xl print:text-4xl', gap: 'gap-8' },
  medium: { name: 'text-2xl print:text-xl', price: 'text-4xl print:text-3xl', gap: 'gap-6' },
  compact: { name: 'text-xl print:text-lg', price: 'text-3xl print:text-2xl', gap: 'gap-y-4' },
} as const;

const FRIDGE_POSTER_STYLES = `
  @media print {
    .poster-qr { width: 40mm !important; height: 40mm !important; }
    .poster-logo { width: 20mm !important; height: 20mm !important; }
  }
  /* Sized here rather than in utilities so the screen and the printed
     sheet keep the same header proportions. */
  .poster-qr { width: 46mm; height: 46mm; }
  .poster-logo { width: 23mm; height: 23mm; }
${FRIDGE_HOW_TO_STYLES}
`;

const FRIDGE_QR_ONLY_STYLES = `
  .qr-sticker-code { width: min(32vw, 52mm); height: min(32vw, 52mm); }
  @media print {
    .qr-sticker-code { width: 48mm !important; height: 48mm !important; }
  }
`;

/**
 * Printable A4 poster for the academy fridge: a header carrying the QR code,
 * and the price list below it as a menu. It renders immersive (outside the
 * global chrome) so the browser prints only the sheet, and the "PDF" is the
 * user hitting print / save-as-PDF.
 *
 * The QR carries no token — it points at the public fridge storefront URL.
 * The menu asks for out-of-stock products too: this sheet is printed once and
 * lives on the fridge for months, so whatever ran out on printing day still
 * belongs on it.
 *
 * A second sheet (FridgeHowToSheet) follows the menu: students tend to open
 * their bank app to read the QR, so that page shows the phone camera doing it.
 */
export default function FridgePosterPage() {
  const { slug } = useParams({ from: ROUTES.FRIDGE_POSTER });

  return <FridgePrintPage slug={slug} qrOnly={false} />;
}

export function FridgeQrPrintPage() {
  const { slug } = useParams({ from: ROUTES.FRIDGE_QR_PRINT });

  return <FridgePrintPage slug={slug} qrOnly />;
}

function FridgePrintPage({ slug, qrOnly }: { slug: string; qrOnly: boolean }) {
  const { data: fridge, isLoading, isError } = useFridge(slug, {
    includeOutOfStock: !qrOnly,
  });
  const [qrCodeState, setQrCodeState] = useState<QrCodeState>('loading');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-7 w-7 animate-spin text-amber-500" />
      </div>
    );
  }

  if (isError || !fridge) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-8 text-center">
        <p className="text-slate-600">Não foi possível carregar as informações desta equipe.</p>
      </div>
    );
  }

  const company = fridge.company;

  // No QR for an inactive fridge: a printed poster pointing at an empty state
  // would only confuse students.
  if (!fridge.enabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-8">
        <div className="max-w-md text-center">
          <Refrigerator className="mx-auto h-10 w-10 text-slate-400" aria-hidden />
          <h1 className="mt-4 font-bebas text-3xl text-slate-950">Geladeira não ativada</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            A Geladeira da {company.name} ainda não está ativa. Ative-a pelo app BlackBelts para gerar o pôster com o QR Code.
          </p>
        </div>
      </div>
    );
  }

  const fridgePath = ROUTES.COMPANY_FRIDGE.replace('$slug', encodeURIComponent(slug));
  const fridgeUrl = new URL(fridgePath, window.location.origin);

  if (qrOnly) {
    // Sticker sheet: the same sticker twice, light on top and dark below, so
    // the academy cuts out whichever reads better on their fridge door.
    return (
      <PrintablePosterLayout qrCodeState={qrCodeState} additionalStyles={FRIDGE_QR_ONLY_STYLES}>
        <main className="flex flex-1 flex-col justify-center gap-5 py-3 print:gap-4">
          <FridgeSticker url={fridgeUrl.toString()} onQrStateChange={setQrCodeState} />
          <FridgeSticker url={fridgeUrl.toString()} variant="dark" />
          <FridgeSticker url={fridgeUrl.toString()} variant="bjj" />

          {qrCodeState === 'error' && (
            <p role="alert" className="text-center text-sm font-medium text-red-700 print:hidden">
              Não foi possível gerar o QR Code. Recarregue a página antes de imprimir.
            </p>
          )}
        </main>
      </PrintablePosterLayout>
    );
  }

  const products = fridge.products;
  const twoColumns = products.length > TWO_COLUMN_THRESHOLD;
  // A short menu top-aligned on A4 leaves the bottom half blank, which reads as
  // an unfinished sheet. Spreading the rows down the page fills it and buys
  // legibility for someone reading it standing at the fridge.
  const spread = !twoColumns && products.length >= SPREAD_THRESHOLD;
  // Two columns stay on the compact scale: a column is ~90mm wide, and at the
  // medium scale a real product name ("Água com Gás 500ml") wraps to two lines,
  // which squeezes the dot leader down to a stub and breaks the row's rhythm.
  // The columns gain their height back through row spacing instead.
  const size = twoColumns ? MENU_SIZES.compact : products.length <= 6 ? MENU_SIZES.roomy : MENU_SIZES.medium;

  return (
    <PrintablePosterLayout
      qrCodeState={qrCodeState}
      additionalStyles={FRIDGE_POSTER_STYLES}
      secondSheet={<FridgeHowToSheet company={company} products={products} url={fridgeUrl.toString()} />}
    >
      <header className="flex flex-col-reverse items-start gap-7 border-b-4 border-yellow-400 pb-7 sm:flex-row sm:items-center sm:justify-between print:flex-row print:items-center print:gap-7 print:pb-6">
        <div className="flex min-w-0 items-center gap-5 print:gap-4">
          {company.logo_url && <img src={company.logo_url} alt="" className="poster-logo shrink-0 rounded-2xl border border-slate-200 object-cover" />}
          <div className="min-w-0">
            <p className="min-w-0 break-words text-sm font-bold uppercase tracking-[0.14em] text-slate-500 print:text-xs">{company.name}</p>
            <h1 className="mt-1.5 font-bebas text-7xl leading-[0.82] tracking-wide text-slate-950 print:text-6xl">Geladeira</h1>
            <p className="mt-2.5 max-w-sm text-base leading-snug text-slate-600 print:mt-2 print:text-sm">
              Escaneie o QR Code, marque o que pegou e pague no Pix na hora.
            </p>
          </div>
        </div>

        <div className="shrink-0 text-center">
          <div className="poster-qr rounded-xl border border-slate-200 bg-white p-2">
            <QrCodeImage value={fridgeUrl.toString()} size={260} className="h-full w-full" alt="QR Code da Geladeira" onStateChange={setQrCodeState} />
          </div>
          <p className="mt-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Escaneie para comprar</p>
        </div>
      </header>

      <main className="flex flex-1 flex-col py-7 print:py-5">
        {products.length === 0 ? (
          <p className="text-base text-slate-500">Os produtos aparecem aqui assim que forem cadastrados no app.</p>
        ) : (
          <ul
            className={
              twoColumns
                ? // Multi-column, not a two-column grid: a grid lays rows out
                  // left-to-right, which makes an alphabetical menu zig-zag
                  // between the columns. Columns flow top-to-bottom, so the
                  // left column runs A–I and the right one picks up from there.
                  'columns-1 gap-12 sm:columns-2 print:columns-2'
                : `flex flex-col ${size.gap} ${spread ? `flex-1 justify-between ${spreadInset(products.length)}` : ''}`
            }
          >
            {products.map((product) => (
              <li key={product.id} className={`flex break-inside-avoid items-end gap-3 ${twoColumns ? 'mb-8 print:mb-7' : ''}`}>
                <span className={`font-bold leading-tight text-slate-950 ${size.name}`}>{product.name}</span>
                <span className="mb-2 min-w-6 flex-1 border-b-2 border-dotted border-slate-300" aria-hidden />
                <span className={`font-bebas leading-none tabular-nums text-slate-950 ${size.price}`}>{formatCurrency(product.price_cents)}</span>
              </li>
            ))}
          </ul>
        )}

        {qrCodeState === 'error' && (
          <p role="alert" className="mt-6 text-sm font-medium text-red-700 print:hidden">
            Não foi possível gerar o QR Code. Recarregue a página antes de imprimir.
          </p>
        )}
      </main>

      <PrintablePosterFooter company={company} />
    </PrintablePosterLayout>
  );
}

const STICKER_STEPS = [
  'Pegue o produto na geladeira.',
  'Escaneie o QR Code.',
  'Marque o que pegou e pague no Pix.',
];

type StickerVariant = 'light' | 'dark' | 'bjj';

// One cut-out sticker: QR on the left, headline + steps on the right. The QR
// keeps its white box in every variant — the code must stay black-on-white to
// scan. Only the first instance reports QR state: all render the same value,
// so one readiness signal is enough for the print button.
//
// The bjj variant swaps the steps for the submission pun ("finalize no Pix")
// and wears a black belt with the red rank bar.
function FridgeSticker({
  url,
  variant = 'light',
  onQrStateChange,
}: {
  url: string;
  variant?: StickerVariant;
  onQrStateChange?: (state: QrCodeState) => void;
}) {
  const dark = variant !== 'light';

  return (
    <section
      className={`flex items-center gap-7 border p-6 print:gap-6 print:p-5 ${
        variant === 'light'
          ? 'border-slate-300 bg-white text-slate-950'
          : variant === 'dark'
            ? 'border-slate-950 bg-slate-950 text-white'
            : 'border-black bg-black text-white'
      }`}
    >
      <div className="shrink-0 text-center">
        <div className="qr-sticker-code rounded-2xl border border-slate-200 bg-white p-2">
          <QrCodeImage
            value={url}
            size={400}
            className="h-full w-full"
            alt="QR Code da Geladeira"
            onStateChange={onQrStateChange}
          />
        </div>
        <p
          className={`mx-auto mt-2 max-w-[52mm] text-[10px] font-bold uppercase tracking-[0.14em] ${
            dark ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          Aponte a câmera do celular
        </p>
      </div>

      {variant === 'bjj' ? (
        <div className="min-w-0 flex-1">
          <p className="font-bebas text-5xl leading-[0.9] tracking-wide print:text-4xl">
            Pegue, aponte a câmera e <span className="text-yellow-400">finalize no Pix.</span>
          </p>
          <p className="mt-3 text-lg font-bold leading-snug text-slate-300 print:text-base">
            Dessa finalização ninguém escapa. Oss!
          </p>
          {/* Black belt, red rank bar — sized like the real thing, knot spared. */}
          <div className="mt-5 flex h-4 overflow-hidden rounded-full border border-slate-700 print:mt-4">
            <div className="flex-1 bg-slate-950" />
            <div className="w-20 bg-red-600" />
            <div className="w-10 bg-slate-950" />
          </div>
        </div>
      ) : (
        <div className="min-w-0 flex-1">
          <p className="font-bebas text-4xl leading-[0.9] tracking-wide sm:text-5xl print:text-4xl">
            Pegue, aponte a câmera e{' '}
            <span className={dark ? 'text-yellow-400' : 'text-amber-500'}>pague com Pix.</span>
          </p>

          <ol className="mt-5 flex flex-col gap-2 print:mt-4">
            {STICKER_STEPS.map((step, index) => (
              <li key={step} className="flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow-400 font-bebas text-xl text-slate-950">
                  {index + 1}
                </span>
                <span className="text-lg font-bold leading-snug print:text-base">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
